import type { CityConfig } from "@/pages/city-template/types";

export const carsonCityConfig: CityConfig = {
  city: "Carson City",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "carson-city",
  route: "/carson-city",
  title: "Emergency Passport Carson City NV | Expedited Passport Nevada State Capital",
  metaDescription: "Emergency passport and expedited passport services in Carson City, NV — as fast as 24 hours. Fast Passport Center serves Nevada's capital city, Douglas County, Dayton, Minden, Gardnerville, and the Carson Valley. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Carson City, Nevada",
  heroSubtitle: "State government business trip to Tokyo or London with two weeks notice? Lake Tahoe family vacation to Europe this summer? Last-minute Mexico getaway from Reno-Tahoe International? Fast Passport Center helps Carson City and Carson Valley residents get expedited passports as fast as 24 hours — entirely remotely. Nevada has no local passport agency, and the nearest is 220 miles over Donner Pass in San Francisco — we serve you without the drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Carson%20City%20Nevada%20state%20capitol%20building%20with%20silver%20dome%20against%20dramatic%20Sierra%20Nevada%20mountain%20backdrop%20at%20golden%20hour%2C%20historic%20downtown%20with%2019th%20century%20brick%20architecture%20and%20the%20Nevada%20State%20Museum%2C%20Eagle%20Valley%20with%20high%20desert%20sagebrush%20landscape%20stretching%20toward%20the%20Carson%20Range%2C%20professional%20architectural%20photography%20with%20warm%20amber%20light%20and%20deep%20blue%20Nevada%20sky%2C%20no%20people%20or%20text%20visible&width=1800&height=700&seq=carson-city-nv-hero-01&orientation=landscape",
  heroImageAlt: "Carson City Nevada state capitol building with Sierra Nevada mountains at sunset",
  geoPosition: "39.1638;-119.7674",
  geoPlacename: "Carson City, Nevada",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "450 Golden Gate Ave, 3rd Floor, San Francisco, CA 94102",
    distance: "About 220 miles west — approximately 4 hours via I-80 over Donner Pass. Winter conditions can add 1–2 hours.",
  },
  nearbyCitiesZips: [
    { name: "Carson City", zips: "89701, 89703, 89705, 89706" },
    { name: "Dayton", zips: "89403" },
    { name: "Minden", zips: "89423" },
    { name: "Gardnerville", zips: "89410, 89460" },
    { name: "Genoa", zips: "89411" },
    { name: "Virginia City", zips: "89440" },
    { name: "Stateline (Lake Tahoe)", zips: "89449" },
    { name: "Zephyr Cove", zips: "89448" },
    { name: "Incline Village", zips: "89451" },
    { name: "Wellington", zips: "89444" },
    { name: "Yerington", zips: "89447" },
    { name: "Washoe Valley", zips: "89704" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Carson City", desc: "Complete your emergency or expedited passport order from anywhere in the capital region — no mountain crossing to San Francisco required." },
    { icon: "ri-truck-line", title: "Overnight Shipping from Carson City or Reno", desc: "Ship your documents from The UPS Store in Carson City or Reno FedEx Office — both support overnight delivery for expedited submissions." },
    { icon: "ri-shield-check-line", title: "Expert Document Review for Government Travelers", desc: "State government employees and legislative staff traveling internationally get document review optimized for official passport requirements." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved San Francisco Passport Agency slots — far faster than driving over Donner Pass for an emergency appointment." },
  ],
  testimonials: [
    {
      name: "Steven M.",
      location: "Carson City, NV",
      rating: 5,
      text: "I work in state government and got a last-minute assignment to a policy conference in London with 10 days notice. My passport was expired. Fast Passport Center renewed it in 5 business days from Carson City. The overnight shipping from the UPS Store was simple. Incredible turnaround for a capital city without its own passport agency.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Maria R.",
      location: "Minden, NV",
      rating: 5,
      text: "We booked a family reunion in Cancun six months ago and I didn't check my passport until packing week — it was expired. Panicked because we're 4+ hours from San Francisco. Fast Passport Center got me an emergency passport in 3 business days. Saved our entire family trip.",
      service: "Emergency Passport Processing",
    },
    {
      name: "David K.",
      location: "Gardnerville, NV",
      rating: 5,
      text: "Needed child passports for our kids' summer study abroad in Spain. Carson City post office acceptance was easy, and Fast Passport Center expedited everything after. Passports arrived in 7 business days. The checklist they gave us was perfect — no errors, no delays.",
      service: "Child Passport Expedited",
    },
  ],
  facilities: [
    {
      id: "carson-city-post-main",
      name: "Carson City Main Post Office",
      type: "USPS Passport Acceptance",
      address: "1111 S Roop St",
      city: "Carson City",
      state: "NV",
      zip: "89701",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "South Carson City — primary passport acceptance for the capital region",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Carson City and western Nevada's capital region. Appointment required. Availability can be limited during peak summer travel season.",
      icon: "ri-mail-line",
    },
    {
      id: "carson-city-clerk",
      name: "Carson City Clerk-Recorder's Office",
      type: "County Clerk Passport Acceptance",
      address: "885 E Musser St, Suite 1025",
      city: "Carson City",
      state: "NV",
      zip: "89701",
      phone: "(775) 887-2087",
      hours: "Mon–Fri 8am–5pm",
      distance: "Downtown Carson City — Carson City Courthouse",
      website: "https://www.carson.org/government/city-clerk-recorder",
      websiteLabel: "Carson City Clerk-Recorder",
      notes: "Carson City Clerk-Recorder accepts passport applications at the Courthouse. Often better appointment availability than the post office.",
      icon: "ri-government-line",
    },
    {
      id: "douglas-county-clerk",
      name: "Douglas County Clerk-Treasurer",
      type: "County Clerk Passport Acceptance",
      address: "1616 8th St",
      city: "Minden",
      state: "NV",
      zip: "89423",
      phone: "(775) 782-9014",
      hours: "Mon–Fri 8am–5pm",
      distance: "Minden — Douglas County Courthouse, about 15 miles south of Carson City via US-395",
      website: "https://www.douglascountynv.gov/government/clerk-treasurer",
      websiteLabel: "Douglas County Clerk",
      notes: "Douglas County Clerk in Minden accepts passport applications. Excellent alternative for Carson Valley residents.",
      icon: "ri-government-line",
    },
    {
      id: "dayton-post",
      name: "Dayton Post Office",
      type: "USPS Passport Acceptance",
      address: "260 Dayton Valley Rd",
      city: "Dayton",
      state: "NV",
      zip: "89403",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Dayton — about 12 miles east of Carson City via US-50",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Small facility serving the Dayton Valley and eastern Carson City corridor. Very limited appointment availability — Carson City preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "reno-post-main",
      name: "Reno Main Post Office",
      type: "USPS Passport Acceptance",
      address: "2000 Vassar St",
      city: "Reno",
      state: "NV",
      zip: "89502",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Reno — about 30 miles north of Carson City via I-580",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Largest acceptance facility in northern Nevada. Reno Main Post Office has the best appointment availability in the Reno-Carson-Tahoe region.",
      icon: "ri-mail-line",
    },
    {
      id: "south-lake-tahoe-post",
      name: "South Lake Tahoe Post Office",
      type: "USPS Passport Acceptance",
      address: "1046 Al Tahoe Blvd",
      city: "South Lake Tahoe",
      state: "CA",
      zip: "96150",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "South Lake Tahoe, CA — about 30 miles southwest via US-50 over Spooner Summit",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative for Carson City residents near Lake Tahoe. California facility — ensure your documents are for a Nevada application.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Carson City, Nevada?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Carson City residents as fast as 24 hours. The San Francisco Passport Agency is about 4 hours from Carson City over Donner Pass (longer in winter) — we serve you entirely remotely via overnight shipping from Carson City or Reno. Start online, we handle the rest.",
    },
    {
      q: "How fast can I get an expedited passport from Carson City, NV?",
      a: "Expedited passport processing from Carson City takes 2–9 business days through Fast Passport Center. Most adult renewals don't require an in-person acceptance visit — ship overnight from The UPS Store in Carson City or Reno FedEx Office.",
    },
    {
      q: "Where is the passport acceptance office in Carson City?",
      a: "Carson City's primary passport acceptance locations are the Carson City Main Post Office (1111 S Roop St) and the Carson City Clerk-Recorder's Office (885 E Musser St). Douglas County Clerk in Minden is an excellent alternative for Carson Valley residents.",
    },
    {
      q: "Does Carson City being the state capital generate passport needs?",
      a: "Yes — state government employees, legislative staff, and agency personnel regularly travel internationally for policy conferences, trade missions, and government meetings in Tokyo, London, Berlin, Ottawa, and Mexico City. These trips often come with short notice (2–6 weeks) — exactly the window for expedited passport processing.",
    },
    {
      q: "How far is Carson City from the San Francisco Passport Agency?",
      a: "Carson City is approximately 220 miles from the San Francisco Passport Agency — about 4 hours via I-80 over Donner Pass. In winter (November–April), snow and chain controls can make this drive 5–7 hours or more. For most urgent travel needs, Fast Passport Center serves Carson City entirely remotely.",
    },
    {
      q: "What airport do Carson City residents use for international travel?",
      a: "Reno-Tahoe International Airport (RNO) is the primary international connection hub for Carson City travelers — about 30 miles north via I-580 (30 minutes). RNO connects to major hubs (SFO, LAX, DEN, SLC, SEA) for international flights. Sacramento International (SMF) is about 130 miles west as an alternative.",
    },
    {
      q: "Does the legislative session create passport rushes in Carson City?",
      a: "Yes — the Nevada Legislature meets every other year (odd years, February–June), and the post-session period (June–August) sees a surge in international travel from state employees, lobbyists, and legislators. Plan passport renewals before the session ends to avoid the summer rush.",
    },
    {
      q: "Can I renew my passport from Carson City without visiting an office?",
      a: "Yes. Most adult Carson City residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from The UPS Store Carson City or Reno FedEx, and we handle the San Francisco agency submission.",
    },
    {
      q: "Can I get a child passport from Carson City?",
      a: "Yes. Child passports require an in-person acceptance visit — Carson City families typically use the Carson City Post Office or Clerk-Recorder's Office. Both parents must typically be present or provide notarized consent.",
    },
    {
      q: "How much does expedited passport processing cost from Carson City?",
      a: "Fast Passport Center pricing includes the U.S. government passport fees plus our courier service fee — which is typically 30–100% lower than FedEx Office or retail passport services. We provide an exact price quote at checkout before any commitment.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Carson City", address: "3300 S Carson St, Carson City, NV 89701", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/storelocator" },
    { name: "The UPS Store — Carson City", address: "3246 N Carson St, Suite 108, Carson City, NV 89706", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 10 AM – 3 PM", website: "https://locations.theupsstore.com/nv/carson-city" },
    { name: "CVS Pharmacy — Carson City", address: "1980 N Carson St, Carson City, NV 89701", hours: "Daily 8 AM – 9 PM", website: "https://www.cvs.com/store-locator" },
    { name: "FedEx Office — Reno", address: "5050 S Virginia St, Reno, NV 89502", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM", website: "https://local.fedex.com/en-us/nv/reno" },
    { name: "Walgreens — Gardnerville", address: "1342 US-395 N, Gardnerville, NV 89410", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/storelocator" },
    { name: "Costco Photo Center — Reno", address: "2200 Harvard Way, Reno, NV 89502", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM", website: "https://www.costco.com/warehouse-locations" },
    { name: "Walgreens — South Reno", address: "10370 S Virginia St, Reno, NV 89511", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/storelocator" },
    { name: "Smith's Food & Drug — Dayton", address: "2200 US-50 E, Dayton, NV 89403", hours: "Daily 6 AM – 11 PM", website: "https://www.smithsfoodanddrug.com/stores" },
  ],
  dmvLocations: [
    {
      name: "Carson City DMV",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Carson City DMV — driver licenses, REAL ID, and state ID cards. Full-service location for the capital region.",
      featured: true,
    },
    {
      name: "Reno DMV",
      address: "305 Galletti Way, Reno, NV 89512",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Reno DMV — largest DMV in northern Nevada. About 30 miles north of Carson City via I-580.",
      featured: false,
    },
    {
      name: "Fallon DMV",
      address: "468 W Williams Ave, Fallon, NV 89406",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Fallon DMV — alternative for east Carson City and Churchill County residents. About 60 miles east via US-50.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RNO",
      name: "Reno-Tahoe International Airport",
      distance: "Primary regional hub — about 30 miles north via I-580 (approximately 30 minutes)",
      address: "2001 E Plumb Ln, Reno, NV 89502",
      note: "Reno-Tahoe International Airport is the primary departure hub for Carson City travelers. RNO connects to major international gateways: SFO, LAX, DEN, SLC, SEA, PHX, and LAS.",
      flightUrl: "https://www.renoairport.com/",
      flightLabel: "RNO Airport Website",
      featured: true,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "About 130 miles west via I-80 (approximately 2 hours 15 minutes)",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Sacramento International offers more direct domestic and international connections than Reno. A viable alternative for Carson City travelers heading to Mexico, Hawaii, or Europe.",
      flightUrl: "https://sacramento.aero/smf",
      flightLabel: "SMF Airport Website",
      featured: false,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "About 240 miles west via I-80 (approximately 4 hours)",
      address: "San Francisco, CA 94128",
      note: "Major international hub with direct flights to Asia, Europe, and Latin America. Long drive from Carson City but offers the most international options.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Carson City & Capital Region",
      description: "State government trip to Tokyo or London departing in days? Lake Tahoe European vacation booked and passport expired? Fast Passport Center handles emergency passport processing for Carson City residents — as fast as 24 hours. Serve entirely remotely.",
      bullets: ["As fast as 24 hours processing", "Reserved San Francisco agency submission", "Dedicated agent and live status tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired and you're in Nevada's capital with no local passport agency? Carson City residents renew fast through Fast Passport Center — ship overnight from The UPS Store Carson City or Reno FedEx.",
      bullets: ["Fastest renewal for capital region residents", "Ship overnight from Carson City or Reno", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Carson City or before a government conference abroad? We guide capital region travelers through DS-64 and expedited replacement — no San Francisco trip required.",
      bullets: ["DS-64 theft/loss reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Carson City Families",
      description: "Carson City and Carson Valley families heading to Canada, Mexico, or European vacations need child passports. We guide you through the Carson City Post Office or Clerk-Recorder acceptance step.",
      bullets: ["Both parents typically required to consent", "Carson City Post Office or Clerk-Recorder guidance", "Expedited after in-person acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Carson City? In-person acceptance at the Carson City Post Office or Clerk-Recorder is required for first-timers. We guide you through every step and expedite from there.",
      bullets: ["Step-by-step acceptance guidance for Carson City facilities", "Photo location guide for Carson City and Reno area", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change passports, damaged passport replacement, second passports for government employees, frequent travelers, and Carson City residents with complex passport needs.",
      bullets: ["Name change after marriage", "Damaged passport replacement", "Second passport for frequent government travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Carson City, Nevada residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Carson City and the Carson Valley region in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Carson City, Nevada — ship overnight from The UPS Store Carson City or Reno FedEx." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Carson City travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Carson City and Carson Valley families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Carson City, Nevada and the capital region." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking International Government Travel Without Checking Passport Validity", description: "Carson City state employees and legislative staff frequently book international conferences and trade missions months ahead but discover expired passports 2–3 weeks before departure." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "Photos from drugstores or self-taken phone photos frequently get rejected. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — costing weeks when you're 4+ hours from the nearest passport agency. Our pre-submission review catches all errors." },
    { icon: "ri-pen-nib-line", title: "Using Hospital Birth Certificates Instead of Certified Copies", description: "Hospital-issued 'certificate of birth' documents are not accepted. You need a certified copy from the Nevada Office of Vital Records or your birth state." },
    { icon: "ri-snowy-line", title: "Underestimating Donner Pass Winter Travel to the San Francisco Agency", description: "In winter (November–April), I-80 over Donner Pass frequently requires chains, closes for hours, or becomes impassable. Don't plan a passport emergency around a mountain crossing — use Fast Passport Center remotely." },
    { icon: "ri-calendar-close-line", title: "Post-Legislative Session Passport Rush", description: "Every odd year, the Nevada legislative session ends in June — creating a sudden spike in international travel from state employees. Renew passports in April–May, before the post-session rush." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Carson City travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — exactly what to gather, with Carson City acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight", description: "Drop off at The UPS Store in Carson City or Reno FedEx Office for overnight delivery. Both are reliable options for capital region residents." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved San Francisco Passport Agency slots — bypassing the public appointment backlog that stretches for weeks." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Door", description: "Your completed passport ships directly to your Carson City or Carson Valley address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to San Francisco — 4+ hours from Carson City",
        "Donner Pass winter conditions can add hours",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Carson City or Reno",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks; can be longer in summer",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait, San Francisco only", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive to San Francisco — 4+ hours, Donner Pass risk in winter", fpc: "Ship overnight from Carson City or Reno" },
    { feature: "Processing Speed", agency: "Strict eligibility requirements", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Capital Region Accessibility", agency: "Not designed for Nevada capital residents", fpc: "Fully remote service via overnight shipping" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 8+ hour SF round trip cost + hotel possible", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Reno", route: "/reno" },
    { name: "Emergency passport help in Sparks", route: "/sparks" },
    { name: "Fast passport renewal for Nevada residents", route: "/nevada" },
    { name: "Rush passport services in Gardnerville", route: "/gardnerville" },
    { name: "Expedited passports statewide in Nevada", route: "/nevada" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from The UPS Store in Carson City or Reno FedEx Office — both available weekday evenings. No visit to our processing facility required for most services.",
  fpcOfficeDistance: "Serving Carson City, the Carson Valley, and the entire capital region remotely",
  serviceAreaIntro: "Whether you are in Carson City, Minden, Gardnerville, Dayton, Genoa, or anywhere in the Carson Valley — Fast Passport Center makes emergency and expedited passport processing simple without a drive over Donner Pass to San Francisco.",
  nearbyCitiesIntro: "We serve all Carson City zip codes and surrounding communities including Dayton, Minden, Gardnerville, Genoa, Stateline, Zephyr Cove, and Washoe Valley.",
  howItWorksIntro: "Getting an emergency or expedited passport from Carson City is simpler than a 4+ hour mountain drive to San Francisco. Most capital region clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Carson City and Carson Valley passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Carson City, Minden, and Gardnerville travelers who needed their passports fast.",
  faqIntro: "The most common questions Carson City and Carson Valley residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { label: "Nevada Vital Records (Birth, Death, Marriage)", href: "https://dpbh.nv.gov/Programs/Vital_Records/Vital_Records_-_Home/" },
    { label: "Carson City Clerk-Recorder Vital Records", href: "https://www.carson.org/government/city-clerk-recorder" },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Carson City and Carson Valley Residents",
    intro: "Carson City is Nevada's state capital — home to the Nevada Legislature, state government agencies, and the Nevada State Museum. Its location in Eagle Valley at the base of the Sierra Nevada, combined with its role as the seat of state government, creates distinctive international travel patterns.",
    cards: [
      {
        icon: "ri-government-line",
        title: "State Government International Travel",
        subtitle: "Nevada's capital generates unique passport demands",
        description: "Carson City's state government workforce — including agency directors, legislative staff, economic development officials, and trade representatives — travels internationally for policy conferences, trade missions, and intergovernmental meetings. Common destinations include Tokyo (technology/economic development), London and Brussels (policy), Mexico City (trade), and Ottawa (cross-border affairs). These trips often come with 2–6 weeks notice — the ideal window for expedited passport processing.",
        tip: "State government travelers in Carson City should maintain a passport with at least 12 months validity at all times. International assignments can arise with as little as 10 days notice.",
      },
      {
        icon: "ri-sun-line",
        title: "Lake Tahoe to Europe — Summer Vacation Season",
        subtitle: "Carson City residents head to Europe after legislative session",
        description: "The Nevada Legislature meets every other year (odd years, February–June). After session ends, there's a notable surge in European travel from Carson City — Italy, France, Spain, and the UK are top destinations. Even in non-session years, summer European vacations are popular. Passport issues are commonly discovered in May–June, creating a predictable pre-summer rush.",
        tip: "If you work in or around state government, check passport validity in April — before the post-session summer travel rush. The 2–9 business day expedited window handles most summer vacation planning.",
      },
      {
        icon: "ri-ship-2-line",
        title: "Mexico & Costa Rica — Winter Sun Escapes",
        subtitle: "High desert winters drive Carson Valley residents south",
        description: "Carson City's high desert location (4,800 feet elevation) means cold winters with occasional snow. Mexico resort destinations (Puerto Vallarta, Cabo, Cancun) and Costa Rica eco-adventures are popular December–March escapes. Trips are typically booked in September–November, with passport issues discovered 3–4 weeks before departure.",
        tip: "Check passport expiration dates in September before booking that Mexico winter escape. The 2–4 weeks between booking and departure is perfect for Fast Passport Center expedited processing.",
      },
      {
        icon: "ri-car-line",
        title: "Canada — Quick I-80/I-5 Road Trips",
        subtitle: "Vancouver BC is accessible from Carson City",
        description: "Carson City's I-80 corridor access makes British Columbia reachable — about 12 hours driving to Vancouver. Many Carson City residents combine BC trips with Seattle or Portland visits. Air travel to Canada requires a passport (land crossings accept enhanced driver's licenses or passport cards).",
        tip: "For air travel to Canada, you MUST have a valid passport. Enhanced driver's licenses work for land crossings only. Check before booking any BC trip with flights.",
      },
    ],
    callout: {
      icon: "ri-government-line",
      title: "Carson City, Nevada — Where the Silver State Governs",
      description: "Carson City sits at the intersection of state government, Sierra Nevada outdoor culture, and high desert living. As Nevada's capital, it generates a steady flow of international government travel — from trade missions to policy conferences — combined with the outdoor and leisure travel patterns of the Carson Valley. Fast Passport Center has served capital region residents with expedited passports for government travel, summer European vacations, winter Mexico escapes, and Tahoe-based international adventures.",
    },
  },
  destinations: {
    title: "Where Carson City Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Carson City and Carson Valley clients. State government travel, Lake Tahoe summer vacation culture, winter Mexico escapes, and Pacific Northwest Canada trips create unique passport urgency situations.",
    stats: [
      { stat: "220 mi", label: "to San Francisco Passport Agency — the closest regional passport office from Carson City" },
      { stat: "30 mi", label: "to Reno-Tahoe International Airport (RNO) — primary regional hub via I-580" },
      { stat: "4+ hrs", label: "to San Francisco — and Donner Pass winter conditions can double that time" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Carson City" },
    ],
    destinations: [
      { name: "Japan & Asia — Government Trade Missions", context: "Nevada economic development and technology trade missions to Tokyo, Seoul, and Shanghai", urgency: "Trade missions confirmed 6–8 weeks out; government travelers need passports with 6+ months validity", icon: "ri-ship-line" },
      { name: "Europe — Post-Session Summer Travel", context: "Carson City's summer European vacation surge after the legislative session ends in June", urgency: "May–June discovery of expired passports; peak demand for 3–5 week expedited processing", icon: "ri-global-line" },
      { name: "Mexico — Winter Sun & Beach Escapes", context: "High desert winter drives Carson Valley residents to Puerto Vallarta, Cabo, and Cancun", urgency: "September–November booking for December–March travel; 3–4 weeks before departure", icon: "ri-sun-cloudy-line" },
      { name: "British Columbia — Vancouver, Victoria", context: "Extended road trips and flights to BC — accessible via Reno flights or I-80/I-5 driving", urgency: "Air travel requires passport; many discover this at booking time for what feels like a local trip", icon: "ri-map-pin-2-line" },
      { name: "London & Brussels — Policy & Government", context: "State government policy conferences and intergovernmental meetings in European capitals", urgency: "2–4 weeks notice for government meetings; expedited processing essential for capital region travelers", icon: "ri-building-2-line" },
      { name: "Costa Rica — Winter Eco-Adventures", context: "Carson Valley's outdoor-oriented community escapes to Costa Rica's rainforests and beaches", urgency: "October–November booking for February–March travel; 3–5 weeks before departure", icon: "ri-leaf-line" },
    ],
    crossLinksTitle: "Explore Nearby Nevada Service Areas",
    crossLinksDesc: "We serve travelers throughout northern Nevada and the capital region with the same expedited passport expertise.",
    crossLinks: [
      { name: "Reno", route: "/reno" },
      { name: "Sparks", route: "/sparks" },
      { name: "Las Vegas", route: "/las-vegas" },
    ],
  },
};